import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import "./SalaryView.css";

const MONTHS = [
  "April","May","June","July","August","September",
  "October","November","December","January","February","March",
];
const MONTH_ABBR: Record<string,string> = {
  April:"Apr",May:"May",June:"Jun",July:"Jul",August:"Aug",September:"Sep",
  October:"Oct",November:"Nov",December:"Dec",January:"Jan",February:"Feb",March:"Mar",
};
const ALL_MONTH_OPTIONS = MONTHS.map((m, i) => ({ label: MONTH_ABBR[m], value: i + 1 }));

// First editable fiscal month index per forecast dimension (same as BudgetEditor)
const DIMENSION_START_MONTH: Record<string, number> = {
  "July Forecast": 3,
  "October Forecast": 6,
  "January Forecast": 9,
};

interface SalaryConfig {
  raise_before_m3: number;
  raise_after_m3: number;
  bip_raise: number;
}
interface Employee {
  id?: number;
  cost_center_number: string;
  employee_name: string;
  job_title: string;
  annual_salary: number;
  employment_type: "Full-Time" | "Part-Time" | "Temp";
  bip_pct: number;
  hire_month: number; // 0 = existing, 1-12 = new hire start month
}

interface SalaryViewProps {
  costCenterNumber: string;
}

// Monthly salary — Temp employees get no raise (flat salary/12)
function computeMonthly(
  salary: number,
  raise_before: number,
  raise_after: number,
  hire_month = 0,            // 0 = existing; 1-12 = new hire (zero out prior months)
  employment_type = "Full-Time"
): Record<string, number> {
  const result: Record<string, number> = {};
  MONTHS.forEach((m, i) => {
    const fiscalMonth = i + 1;
    if (hire_month > 0 && fiscalMonth < hire_month) {
      result[m] = 0;
    } else {
      const raise = employment_type === "Temp" ? 0 : (i < 2 ? raise_before : raise_after);
      result[m] = Math.round(salary * (1 + raise) / 12);
    }
  });
  return result;
}

const fmt = (n: number) =>
  n === 0 ? "–" : "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0 });

const emptyEmployee = (cc: string): Employee => ({
  cost_center_number: cc,
  employee_name: "",
  job_title: "",
  annual_salary: 0,
  employment_type: "Full-Time",
  bip_pct: 0,
  hire_month: 0,
});

const emptyNewHire = (cc: string): Employee => ({
  ...emptyEmployee(cc),
  hire_month: 1,
});

const SalaryView: React.FC<SalaryViewProps> = ({ costCenterNumber }) => {
  const [config, setConfig] = useState<SalaryConfig>({ raise_before_m3: 0, raise_after_m3: 0.035, bip_raise: 0 });
  const [existing, setExisting] = useState<Employee[]>([]);
  const [newHires, setNewHires] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [forecastDim, setForecastDim] = useState<string | null>(null);
  const [forecastActive, setForecastActive] = useState(false);

  // Snapshot of last-saved state for dirty detection
  const savedSnapshot = useRef<string>("");

  const serializeEmployees = (ex: Employee[], nh: Employee[]) =>
    JSON.stringify([...ex.map(e => ({ ...e, hire_month: e.hire_month || 0 })), ...nh]);

  const isDirty = !loading && serializeEmployees(existing, newHires) !== savedSnapshot.current;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, empRes] = await Promise.all([
        api.get("/salary/config"),
        api.get(`/salary/employees?cost_center=${costCenterNumber}`),
      ]);
      setConfig(cfgRes.data);
      const all: Employee[] = empRes.data;
      const ex = all.filter(e => !e.hire_month || e.hire_month === 0);
      const nh = all.filter(e => e.hire_month && e.hire_month > 0);
      setExisting(ex);
      setNewHires(nh);
      savedSnapshot.current = serializeEmployees(ex, nh);
    } catch {
      setFeedback({ ok: false, msg: "Failed to load salary data." });
    } finally {
      setLoading(false);
    }
  }, [costCenterNumber]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/settings/forecast-config")
      .then(res => {
        setForecastActive(!!res.data.active);
        if (res.data.dimension) setForecastDim(res.data.dimension);
      })
      .catch(() => {});
  }, []);

  // Month options restricted to the current forecast window (forward-looking only)
  const monthOptions = (() => {
    const startIdx = forecastDim ? (DIMENSION_START_MONTH[forecastDim] ?? 0) : 0;
    return ALL_MONTH_OPTIONS.filter(o => o.value > startIdx);
  })();
  const firstAllowedMonth = monthOptions[0]?.value ?? 1;

  const addExisting = () => setExisting(prev => [...prev, emptyEmployee(costCenterNumber)]);
  const addNewHire  = () => setNewHires(prev => [...prev, { ...emptyNewHire(costCenterNumber), hire_month: firstAllowedMonth }]);

  const removeExisting = (idx: number) => setExisting(prev => prev.filter((_, i) => i !== idx));
  const removeNewHire  = (idx: number) => setNewHires(prev => prev.filter((_, i) => i !== idx));

  const updateExisting = (idx: number, field: keyof Employee, value: string | number) =>
    setExisting(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  const updateNewHire  = (idx: number, field: keyof Employee, value: string | number) =>
    setNewHires(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    const all = [
      ...existing,
      ...newHires,
    ];
    try {
      await api.post("/salary/employees", {
        cost_center: costCenterNumber,
        employees: all.map(e => ({
          employee_name:   e.employee_name,
          job_title:       e.job_title,
          annual_salary:   e.annual_salary,
          employment_type: e.employment_type,
          bip_pct:         e.bip_pct,
          hire_month:      e.hire_month,
        })),
      });
      setFeedback({ ok: true, msg: "Saved successfully." });
      load();
    } catch {
      setFeedback({ ok: false, msg: "Save failed. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  // ── Compute monthly values + summary rows ──
  const existingWithMonthly = existing.map(emp => ({
    ...emp,
    monthly: computeMonthly(emp.annual_salary, config.raise_before_m3, config.raise_after_m3, emp.hire_month, emp.employment_type),
  }));
  const newHiresWithMonthly = newHires.map(emp => ({
    ...emp,
    monthly: computeMonthly(emp.annual_salary, config.raise_before_m3, config.raise_after_m3, emp.hire_month, emp.employment_type),
  }));

  const allWithMonthly = [...existingWithMonthly, ...newHiresWithMonthly];

  const monthlyTotals: Record<string, number> = {};
  const monthlyHeadcount: Record<string, number> = {};
  MONTHS.forEach(m => { monthlyTotals[m] = 0; monthlyHeadcount[m] = 0; });
  allWithMonthly.forEach((emp, i) => {
    const isNewHire = i >= existingWithMonthly.length;
    MONTHS.forEach((m, mIdx) => {
      monthlyTotals[m] += emp.monthly[m];
      const fiscalMonth = mIdx + 1;
      const hm = isNewHire ? (emp.hire_month || 1) : 0;
      if (emp.employment_type === "Full-Time" && (hm === 0 || fiscalMonth >= hm)) {
        monthlyHeadcount[m]++;
      }
    });
  });
  const annualTotal = MONTHS.reduce((s, m) => s + monthlyTotals[m], 0);

  const editingDisabled = !forecastActive;

  if (loading) return <div className="salary-loading">Loading salary data…</div>;

  const employeeTableBody = (
    rows: typeof existingWithMonthly,
    onUpdate: (idx: number, field: keyof Employee, value: string | number) => void,
    onRemove: (idx: number) => void,
    isNewHire: boolean
  ) => rows.map((emp, idx) => (
    <tr key={idx} className="salary-row">
      <td className="col-del">
        <button className="salary-del-btn" onClick={() => onRemove(idx)} title="Remove" disabled={editingDisabled}>
          <span className="material-icons">close</span>
        </button>
      </td>
      <td className="col-center">
        <span className="salary-cc-badge">{costCenterNumber}</span>
      </td>
      <td className="col-name">
        <input
          className="salary-input"
          value={emp.employee_name}
          placeholder={isNewHire ? "New hire name (optional)" : "Employee name"}
          onChange={e => onUpdate(idx, "employee_name", e.target.value)}
          disabled={editingDisabled}
        />
      </td>
      <td className="col-title">
        <input
          className="salary-input"
          value={emp.job_title}
          placeholder="Job title"
          onChange={e => onUpdate(idx, "job_title", e.target.value)}
          disabled={editingDisabled}
        />
      </td>
      <td className="col-salary">
        <input
          className="salary-input salary-input--num"
          type="number" min={0} step={1000}
          value={emp.annual_salary || ""}
          placeholder="0"
          onChange={e => onUpdate(idx, "annual_salary", parseFloat(e.target.value) || 0)}
          disabled={editingDisabled}
        />
      </td>
      <td className="col-type">
        <select
          className="salary-select"
          value={emp.employment_type}
          onChange={e => onUpdate(idx, "employment_type", e.target.value as Employee["employment_type"])}
          disabled={editingDisabled}
        >
          <option value="Full-Time">Full-Time</option>
          <option value="Part-Time">Part-Time</option>
          <option value="Temp">Temp</option>
        </select>
      </td>
      <td className="col-hire-month">
        <select
          className="salary-select"
          value={emp.hire_month}
          onChange={e => onUpdate(idx, "hire_month", parseInt(e.target.value))}
          disabled={editingDisabled}
        >
          {!isNewHire && <option value={0}>Full Year</option>}
          {ALL_MONTH_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </td>
      {MONTHS.map(m => (
        <td key={m} className="col-month salary-calc-cell">
          {emp.monthly[m] > 0 ? fmt(emp.monthly[m]) : <span style={{ color: "#d1d5db" }}>–</span>}
        </td>
      ))}
      <td className="col-total salary-calc-cell salary-row-total">
        {fmt(MONTHS.reduce((s, m) => s + emp.monthly[m], 0))}
      </td>
    </tr>
  ));

  return (
    <div className="salary-view">
      {editingDisabled && (
        <div className="salary-locked-banner">
          <span className="material-icons" style={{ fontSize: 16, verticalAlign: "middle", marginRight: 6 }}>lock</span>
          Salary editing is currently disabled. Contact the Finance Admin to open forecast planning.
        </div>
      )}
      {/* Raise config banner */}
      <div className="salary-header">
        <div className="salary-header-sub-info">Account 60010 — Salaries-Employees</div>
        <div className="salary-raise-banner">
          <span className="salary-raise-label">Raise % Apr/May</span>
          <span className="salary-raise-value">{(config.raise_before_m3 * 100).toFixed(2)}%</span>
          <span className="salary-raise-sep">·</span>
          <span className="salary-raise-label">Raise % Jun–Mar</span>
          <span className="salary-raise-value">{(config.raise_after_m3 * 100).toFixed(2)}%</span>
          <span className="salary-raise-note">set by Finance Admin</span>
        </div>
      </div>

      <div className="salary-table-wrap">
        <table className="salary-table">
          <thead>
            <tr className="salary-thead-top">
              <th className="col-del" />
              <th className="col-center">Center</th>
              <th className="col-name">Name</th>
              <th className="col-title">Job Title</th>
              <th className="col-salary">Annual Salary</th>
              <th className="col-type">Type</th>
              <th className="col-hire-month">Start Month</th>
              {MONTHS.map(m => <th key={m} className="col-month">{MONTH_ABBR[m]}</th>)}
              <th className="col-total">Total FY</th>
            </tr>
          </thead>
          <tbody>
            {/* ── Existing Employees ── */}
            <tr className="salary-section-header-row">
              <td colSpan={19} className="salary-section-label">
                <span className="salary-section-label-text">Existing Employees</span>
                <button className="salary-section-add-btn" onClick={addExisting} disabled={editingDisabled}>
                  <span className="material-icons">add</span>
                  Add Existing Employee
                </button>
              </td>
            </tr>
            {existing.length === 0 ? (
              <tr>
                <td colSpan={19} className="salary-empty-row">
                  No existing employees added yet.
                </td>
              </tr>
            ) : (
              employeeTableBody(existingWithMonthly, updateExisting, removeExisting, false)
            )}

            {/* ── Spacer between sections ── */}
            <tr className="salary-section-spacer"><td colSpan={19} /></tr>

            {/* ── New Hire Planning ── */}
            <tr className="salary-section-header-row salary-newhire-header">
              <td colSpan={19} className="salary-section-label">
                <span className="salary-section-label-text">
                  <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>person_add</span>
                  Budget New Hires
                </span>
                <button className="salary-section-add-btn salary-section-add-btn--newhire" onClick={addNewHire} disabled={editingDisabled}>
                  <span className="material-icons">add</span>
                  Add New Hire
                </button>
              </td>
            </tr>
            {newHires.length === 0 ? (
              <tr>
                <td colSpan={19} className="salary-empty-row">
                  No new hires planned.
                </td>
              </tr>
            ) : (
              employeeTableBody(newHiresWithMonthly, updateNewHire, removeNewHire, true)
            )}
          </tbody>

          {/* Summary rows */}
          <tfoot>
            <tr className="salary-summary-row salary-total-row">
              <td colSpan={7} className="salary-summary-label">TOTAL SALARY</td>
              {MONTHS.map(m => (
                <td key={m} className="col-month salary-calc-cell">
                  {monthlyTotals[m] === 0 ? "–" : "$" + monthlyTotals[m].toLocaleString()}
                </td>
              ))}
              <td className="col-total salary-calc-cell salary-row-total">
                {annualTotal === 0 ? "–" : "$" + annualTotal.toLocaleString()}
              </td>
            </tr>
            <tr className="salary-summary-row salary-hc-row">
              <td colSpan={7} className="salary-summary-label">Full-Time Headcount</td>
              {MONTHS.map(m => (
                <td key={m} className="col-month salary-calc-cell">{monthlyHeadcount[m]}</td>
              ))}
              <td className="col-total salary-calc-cell" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Note */}
      <div className="salary-note">
        <span className="material-icons salary-note-icon">info_outline</span>
        <div className="salary-note-content">
          <p><strong>How salary is computed:</strong> Monthly salary = (Annual Salary x (1 + Raise %)) / 12. Raise % for Apr/May and Jun-Mar are set by Finance Admin and shown above. Temp employees receive no raise.</p>
          <p><strong>Start month:</strong> Selecting a specific month zeros out salary for all prior months. Use "Full Year" for employees present the entire fiscal year.</p>
          <p><strong>Budget New Hires:</strong> Plan future hires here by entering the expected start month and annual salary. Once the hire is complete, delete them from Budget New Hires and add them to the Existing Employees list.</p>
        </div>
      </div>

      {/* Actions */}
      <div className="salary-actions">
        <div>
          {isDirty && !saving && (
            <span className="salary-unsaved-hint">
              <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>edit</span>
              You have unsaved changes
            </span>
          )}
        </div>
        <div className="salary-actions-right">
          {feedback && (
            <span className={`salary-feedback ${feedback.ok ? "salary-feedback--ok" : "salary-feedback--err"}`}>
              {feedback.msg}
            </span>
          )}
          <button
            className={`salary-save-btn${isDirty ? " salary-save-btn--active" : ""}`}
            onClick={handleSave}
            disabled={saving || !isDirty || editingDisabled}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalaryView;
